import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../Dashboard/Dashboard.css';
import './Analytics.css';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import PageInstructionBanner from '../common/PageInstructionBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../services/analytics';
import { listEvents } from '../../services/events';
import { listBooths } from '../../services/booths';
import videoCallService from '../../services/videoCall';
import { Select } from '../UI/FormComponents';
import { useRecruiterBooth } from '../../hooks/useRecruiterBooth';
import axios from 'axios';

const CHAT_STATUS_LABELS = { online: 'Online', away: 'Away', meeting: 'Meeting', offline: 'Offline' };

// "12 min 05 sec" / "1 h 02 min 05 sec"
const formatElapsed = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, '0');
    if (hours > 0) return `${hours} h ${pad(minutes)} min ${pad(seconds)} sec`;
    if (minutes > 0) return `${minutes} min ${pad(seconds)} sec`;
    return `${seconds} sec`;
};

export default function Analytics() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const { booth: assignedBooth, event: assignedEvent, loading: assignedBoothLoading } = useRecruiterBooth();

    useEffect(() => {
        if (!loading) {
            if (!user) {
                navigate('/login', { replace: true });
            } else if (!['Admin', 'GlobalSupport', 'Support', 'Recruiter'].includes(user.role)) {
                navigate('/dashboard', { replace: true});
            }
        }
    }, [user, loading, navigate]);

    const [activeTab, setActiveTab] = useState(user?.role === 'Recruiter' ? 'report' : 'overview');
    const [loadingData, setLoadingData] = useState(true);
    const [events, setEvents] = useState([]);
    const [boothOptions, setBoothOptions] = useState([]);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // Overview data
    const [overview, setOverview] = useState(null);

    // Report data
    const [reportData, setReportData] = useState(null);

    // Live stats
    const [liveStats, setLiveStats] = useState(null);
    const [liveStatsLoading, setLiveStatsLoading] = useState(false);
    const [endingCallSessionId, setEndingCallSessionId] = useState(null);
    // "Time in Call" ticks from the server clock: offset = server time - this browser's time
    const [clockOffsetMs, setClockOffsetMs] = useState(0);
    const [nowMs, setNowMs] = useState(() => Date.now());
    // Live Stats filters, applied on this screen (the top Filters bar reloads from the server)
    const [liveBoothFilter, setLiveBoothFilter] = useState('');
    const [liveNameFilter, setLiveNameFilter] = useState('');
    const [liveInterpreterOnly, setLiveInterpreterOnly] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        eventId: '',
        boothId: '',
        startDate: '',
        endDate: ''
    });

    const isSupportRole = user?.role === 'Support';
    const isRecruiterRole = user?.role === 'Recruiter';
    const canFilterBooth = ['Admin', 'GlobalSupport'].includes(user?.role);
    const canForceEndCall = ['Admin', 'GlobalSupport'].includes(user?.role);
    const recruiterAssignedEventIds = useMemo(
        () => (user?.assignedEvents || []).map((e) => (e?._id || e).toString()),
        [user?.assignedEvents]
    );
    const recruiterEventOptions = isRecruiterRole && recruiterAssignedEventIds.length > 0
        ? events.filter((e) => recruiterAssignedEventIds.includes(e._id.toString()))
        : events;

    const showToast = useCallback((message, type = 'info') => {
        toast.show(message, { type, duration: 3000 });
    }, [toast]);

    // Load events for filter dropdown
    useEffect(() => {
        const loadEvents = async () => {
            try {
                const response = await listEvents({ page: 1, limit: 100 });
                setEvents(response.events || []);
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };
        if (user) {
            loadEvents();
        }
    }, [user]);

    // Load booths for admin/global support filters
    useEffect(() => {
        if (!canFilterBooth) {
            return;
        }
        const loadBoothsOptions = async () => {
            try {
                const response = await listBooths({
                    page: 1,
                    limit: 200,
                    eventId: filters.eventId || undefined
                });
                setBoothOptions(response.booths || []);
            } catch (error) {
                console.error('Error loading booths:', error);
                setBoothOptions([]);
            }
        };
        loadBoothsOptions();
    }, [canFilterBooth, filters.eventId]);

    // Support and Recruiter roles: lock filters to assigned booth/event
    useEffect(() => {
        if ((!isSupportRole && !isRecruiterRole) || assignedBoothLoading) {
            return;
        }

        if (assignedBooth?._id) {
            setBoothOptions([assignedBooth]);
        }

        setFilters(prev => {
            let updated = false;
            const next = { ...prev };

            if (!prev.boothId && assignedBooth?._id) {
                next.boothId = assignedBooth._id;
                updated = true;
            }

            if (isRecruiterRole && recruiterAssignedEventIds.length > 0) {
                if (!prev.eventId || !recruiterAssignedEventIds.includes(prev.eventId.toString())) {
                    next.eventId = recruiterAssignedEventIds[0];
                    updated = true;
                }
            } else if (assignedEvent?._id && prev.eventId !== assignedEvent._id) {
                next.eventId = assignedEvent._id;
                updated = true;
            }

            return updated ? next : prev;
        });

    }, [isSupportRole, isRecruiterRole, assignedBoothLoading, assignedBooth, assignedEvent, recruiterAssignedEventIds]);

    // Load overview data
    const loadOverview = useCallback(async () => {
        try {
            setLoadingData(true);
            const data = await analyticsAPI.getOverview(filters);
            setOverview(data);
        } catch (error) {
            console.error('Error loading overview:', error);
            showToast('Failed to load analytics overview', 'error');
        } finally {
            setLoadingData(false);
        }
    }, [filters, showToast]);

    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoadingData(true);
            const data = await analyticsAPI.getBooths(filters);
            setReportData(data);
        } catch (error) {
            console.error('Error loading report:', error);
            showToast('Failed to load report', 'error');
        } finally {
            setLoadingData(false);
        }
    }, [filters, showToast]);

    const loadLiveStats = useCallback(async (showSpinner = false) => {
        try {
            if (showSpinner) {
                setLiveStatsLoading(true);
            }
            const data = await analyticsAPI.getLiveStats({
                eventId: filters.eventId,
                boothId: filters.boothId
            });
            setLiveStats(data);
            if (data?.serverTime) {
                const serverMs = new Date(data.serverTime).getTime();
                if (!Number.isNaN(serverMs)) {
                    setClockOffsetMs(serverMs - Date.now());
                }
            }
            setNowMs(Date.now());
        } catch (error) {
            console.error('Error loading live stats:', error);
            showToast('Failed to load live stats', 'error');
        } finally {
            if (showSpinner) {
                setLiveStatsLoading(false);
            }
        }
    }, [filters.eventId, filters.boothId, showToast]);

    const handleForceEndCall = useCallback(async (meeting) => {
        const sessionId = meeting?.sessionId;
        if (!sessionId) {
            showToast('Unable to identify this call', 'error');
            return;
        }

        const participants = [meeting.jobSeeker, meeting.recruiter, meeting.interpreter]
            .filter((name) => name && name !== '—')
            .join(', ');
        const confirmMsg = participants
            ? `Force end only this call (${participants})? Other active calls will not be affected.`
            : 'Force end only this call? Other active calls will not be affected.';

        if (!window.confirm(confirmMsg)) {
            return;
        }

        setEndingCallSessionId(sessionId);
        try {
            if (sessionId.startsWith('video:')) {
                const callId = sessionId.slice('video:'.length);
                await videoCallService.endCall(callId);
            } else if (sessionId.startsWith('meeting:')) {
                const meetingId = sessionId.slice('meeting:'.length);
                await axios.post(`/api/calls/${meetingId}/end`);
            } else {
                showToast('Unsupported call type', 'error');
                return;
            }

            showToast('Call ended successfully', 'success');
            await loadLiveStats();
        } catch (error) {
            console.error('Error force-ending call:', error);
            showToast(error?.error || error?.message || 'Failed to end call', 'error');
        } finally {
            setEndingCallSessionId(null);
        }
    }, [loadLiveStats, showToast]);

    // Load data based on active tab
    useEffect(() => {
        if (activeTab === 'overview') {
            loadOverview();
        } else if (activeTab === 'report') {
            loadReport();
        } else if (activeTab === 'live-stats') {
            loadLiveStats(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters]);

    useEffect(() => {
        if (activeTab !== 'live-stats') {
            return undefined;
        }

        const intervalId = setInterval(() => {
            loadLiveStats();
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters.eventId, filters.boothId]);

    const activeCallCount = liveStats?.calls?.meetings?.length || 0;
    const queueEntryCount = liveStats?.queue?.entries?.length || 0;

    // Tick once a second while there are calls or waits to time. Not a live region: screen
    // readers read the current value when the cell is reached, instead of hearing it every second.
    useEffect(() => {
        if (activeTab !== 'live-stats' || (activeCallCount === 0 && queueEntryCount === 0)) {
            return undefined;
        }
        const intervalId = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(intervalId);
    }, [activeTab, activeCallCount, queueEntryCount]);

    const elapsedSince = (value) => (
        value ? formatElapsed(nowMs + clockOffsetMs - new Date(value).getTime()) : '—'
    );

    // Longest-running call first
    const sortedMeetings = useMemo(() => (
        [...(liveStats?.calls?.meetings || [])].sort(
            (a, b) => new Date(a.joinedAt || 0) - new Date(b.joinedAt || 0)
        )
    ), [liveStats]);

    // Every booth that appears anywhere in the live data, for the Booth filter
    const liveBoothOptions = useMemo(() => {
        const booths = new Map();
        const add = (id, name) => {
            if (id && !booths.has(String(id))) booths.set(String(id), name || 'Unnamed booth');
        };
        (liveStats?.calls?.meetings || []).forEach(m => add(m.boothId, m.boothName));
        (liveStats?.queue?.byBooth || []).forEach(b => add(b.boothId, b.boothName));
        (liveStats?.queue?.entries || []).forEach(e => add(e.boothId, e.boothName));
        (liveStats?.onlineUsers?.users || []).forEach(u => add(u.assignedBooth, u.boothName));
        return Array.from(booths, ([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [liveStats]);

    // A booth that has left the live data would otherwise hide every row
    useEffect(() => {
        if (liveBoothFilter && liveStats && !liveBoothOptions.some(o => o.value === liveBoothFilter)) {
            setLiveBoothFilter('');
        }
    }, [liveBoothFilter, liveBoothOptions, liveStats]);

    const liveFiltersActive = Boolean(liveBoothFilter || liveNameFilter.trim() || liveInterpreterOnly);
    const isLookingForInterpreter = (meeting) => meeting.interpreterRequest?.status === 'waiting';
    const matchesLiveBooth = (boothId) => !liveBoothFilter || String(boothId || '') === liveBoothFilter;
    const matchesJobSeekerName = (name) => {
        const query = liveNameFilter.trim().toLowerCase();
        return !query || String(name || '').toLowerCase().includes(query);
    };

    const visibleMeetings = sortedMeetings.filter(m => (
        matchesLiveBooth(m.boothId) && matchesJobSeekerName(m.jobSeeker) && (!liveInterpreterOnly || isLookingForInterpreter(m))
    ));
    const queueByBooth = liveStats?.queue?.byBooth || [];
    const visibleQueueByBooth = queueByBooth.filter(b => (
        matchesLiveBooth(b.boothId) && (!liveInterpreterOnly || b.needInterpreter > 0)
    ));
    const queueEntries = liveStats?.queue?.entries || [];
    const visibleQueueEntries = queueEntries.filter(e => (
        matchesLiveBooth(e.boothId) && matchesJobSeekerName(e.jobSeeker?.name) && (!liveInterpreterOnly || Boolean(e.interpreterCategory))
    ));
    const onlineUserRows = liveStats?.onlineUsers?.users || [];
    const visibleOnlineUsers = onlineUserRows.filter(u => matchesLiveBooth(u.assignedBooth));

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            if (key === 'eventId') {
                if (prev.eventId === value) {
                    return prev;
                }
                const next = { ...prev, eventId: value };
                if (canFilterBooth) {
                    next.boothId = '';
                }
                return next;
            }

            if (prev[key] === value) {
                return prev;
            }

            return { ...prev, [key]: value };
        });
    };

    const handleExport = async () => {
        try {
            if (activeTab === 'report') {
                await analyticsAPI.exportCSV('booths', filters);
                showToast('Export started', 'success');
            }
        } catch (error) {
            console.error('Error exporting:', error);
            showToast('Failed to export data', 'error');
        }
    };

    const formatDuration = (minutes) => {
        if (!minutes || minutes === 0) return '0 sec';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        const secs = Math.floor((minutes % 1) * 60);

        if (hours > 0) {
            return `${hours} min ${mins} sec`;
        } else if (mins > 0) {
            return `${mins} min ${secs} sec`;
        } else {
            return `${secs} sec`;
        }
    };

    const formatDateTime = (value) => {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString();
        } catch (error) {
            return '—';
        }
    };

    if (loading) {
        return <div className="dashboard-loading">Loading...</div>;
    }

    return (
        <div className="dashboard">
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <AdminHeader />
            <div className="dashboard-layout">
                <AdminSidebar active="analytics" />
                <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                    <div className="dashboard-content">
                        <div className="analytics-container">
                            <PageInstructionBanner screen="analytics" />
                            {/* Page Header 
                            <div className="page-header">
                                <h1>Analytics & Reports</h1>
                                {(user?.role === 'Support' || user?.role === 'Recruiter') && (
                                    <p className="page-subtitle">Viewing data for your assigned booth</p>
                                )}
                            </div>
                            */}

                            {/* Tabs */}
                            <div className="analytics-tabs">
                                {!isRecruiterRole && (
                                    <button
                                        className={`analytics-tab ${activeTab === 'overview' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('overview')}
                                    >
                                        Overview
                                    </button>
                                )}
                                <button
                                    className={`analytics-tab ${activeTab === 'report' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('report')}
                                >
                                    Report
                                </button>
                                {!isRecruiterRole && (
                                    <button
                                        className={`analytics-tab ${activeTab === 'live-stats' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('live-stats')}
                                    >
                                        Live Stats
                                    </button>
                                )}
                            </div>

                            {/* Filters */}
                            <div className="analytics-filters">
                                <div
                                    className="filters-header"
                                    onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setFiltersCollapsed(!filtersCollapsed);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <h3>Filters</h3>
                                    <span className="filters-toggle">
                                        {filtersCollapsed ? '▼' : '▲'}
                                    </span>
                                </div>
                                {!filtersCollapsed && (
                                    <>
                                        <div className="filters-row top-row">
                                            <div className="filter-group">
                                                <label htmlFor="event-filter">Filter by Event:</label>
                                                <Select
                                                    id="event-filter"
                                                    value={filters.eventId}
                                                    onChange={(e) => handleFilterChange('eventId', e.target.value)}
                                                    disabled={isRecruiterRole && recruiterEventOptions.length <= 1}
                                                    options={[
                                                        ...(!isRecruiterRole ? [{ value: '', label: 'All Events' }] : []),
                                                        ...recruiterEventOptions.map(e => ({ value: e._id, label: e.name }))
                                                    ]}
                                                />
                                            </div>
                                            {canFilterBooth && (
                                                <div className="filter-group">
                                                    <label htmlFor="booth-filter">Filter by Booth:</label>
                                                    <Select
                                                        id="booth-filter"
                                                        value={filters.boothId}
                                                        onChange={(e) => handleFilterChange('boothId', e.target.value)}
                                                        options={[
                                                            { value: '', label: 'All Booths' },
                                                            ...boothOptions.map(booth => ({
                                                                value: booth._id || booth.id,
                                                                label: booth.name
                                                            }))
                                                        ]}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className={`filters-row bottom-row ${activeTab === 'report' ? 'with-button' : ''}`}>
                                            <div className="filter-group">
                                                <label htmlFor="start-date">Start Date:</label>
                                                <input
                                                    id="start-date"
                                                    type="date"
                                                    value={filters.startDate}
                                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label htmlFor="end-date">End Date:</label>
                                                <input
                                                    id="end-date"
                                                    type="date"
                                                    value={filters.endDate}
                                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                                />
                                            </div>
                                            {activeTab === 'report' && (
                                                <button className="btn-export" onClick={handleExport} disabled={loadingData}>
                                                    Export Filtered Results
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Content */}
                            {loadingData ? (
                                <div className="analytics-loading">Loading analytics data...</div>
                            ) : (
                                <>
                                    {/* Overview Tab */}
                                    {activeTab === 'overview' && overview && (
                                        <div className="analytics-overview">
                                            <div className="stats-grid">
                                                <div className="stat-card">
                                                    <h3>Total Meetings</h3>
                                                    <div className="stat-value">{overview.meetings?.total || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Completed Meetings</h3>
                                                    <div className="stat-value">{overview.meetings?.completed || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Dropped Meetings</h3>
                                                    <div className="stat-value">{overview.meetings?.dropped || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Avg Duration</h3>
                                                    <div className="stat-value">
                                                        {formatDuration(overview.meetings?.averageDuration || 0)}
                                                    </div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Meetings &gt; 3 Min</h3>
                                                    <div className="stat-value">{overview.meetings?.meetingsOver3Min || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>With Interpreter</h3>
                                                    <div className="stat-value">{overview.meetings?.totalWithInterpreter || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Total Queue Visits</h3>
                                                    <div className="stat-value">{overview.queue?.totalVisits || 0}</div>
                                                </div>
                                                <div className="stat-card">
                                                    <h3>Unique Queue Visits</h3>
                                                    <div className="stat-value">{overview.queue?.uniqueVisits || 0}</div>
                                                </div>
                                            </div>

                                            {overview.users && ['Admin', 'GlobalSupport'].includes(user?.role) && (
                                                <div className="users-stats-section">
                                                    <h2>User Statistics</h2>
                                                    <div className="stats-grid">
                                                        <div className="stat-card">
                                                            <h3>Total Users</h3>
                                                            <div className="stat-value">{overview.users.totalUsers || 0}</div>
                                                        </div>
                                                        <div className="stat-card">
                                                            <h3>Active Users</h3>
                                                            <div className="stat-value">{overview.users.activeUsers || 0}</div>
                                                        </div>
                                                    </div>
                                                    {overview.users.byRole && (
                                                        <div className="role-breakdown">
                                                            <h3>Users by Role</h3>
                                                            <div className="table-scroll-wrap" data-dual-scroll-target="true">
                                                                <table className="data-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Role</th>
                                                                            <th>Total</th>
                                                                            <th>Active</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(overview.users.byRole).map(([role, stats]) => (
                                                                            <tr key={role}>
                                                                                <td>{role}</td>
                                                                                <td>{stats.total}</td>
                                                                                <td>{stats.active}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Report Tab */}
                                    {activeTab === 'report' && (
                                        <div className="analytics-booth-report">
                                            {reportData?.booths && reportData.booths.length > 0 ? (
                                                <>
                                                    <h2>Report</h2>
                                                    <div className="booth-report-table" data-dual-scroll-target="true">
                                                        <table className="data-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Booth</th>
                                                                <th>Event</th>
                                                                <th>Job Seeker Interest</th>
                                                                <th>Unique Queue Visits</th>
                                                                <th>Total Queue Visits</th>
                                                                <th>Unique Meetings</th>
                                                                <th>Total Job Seeker Meetings</th>
                                                                <th># Dropped Meetings</th>
                                                                <th>Meetings &gt; 3 Min</th>
                                                                <th>Average Meeting Time</th>
                                                                <th>Avg Meetings per Recruiter</th>
                                                                <th>Interpreter Meetings</th>
                                                                <th>Interpreter Time</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {reportData.booths.map((booth, index) => (
                                                                <tr key={booth._id || index}>
                                                                    <td>{booth.name}</td>
                                                                    <td>{booth.eventName || 'N/A'}</td>
                                                                    <td>{booth.jobSeekerInterest || 0}</td>
                                                                    <td>{booth.uniqueQueueVisits || 0}</td>
                                                                    <td>{booth.totalQueueVisits || 0}</td>
                                                                    <td>{booth.uniqueMeetings || 0}</td>
                                                                    <td>{booth.totalJobSeekerMeetings || 0}</td>
                                                                    <td>{booth.droppedMeetings || 0}</td>
                                                                    <td>{booth.meetingsOver3Min || 0}</td>
                                                                    <td>{formatDuration(booth.averageMeetingTime || 0)}</td>
                                                                    <td>{booth.avgMeetingsPerRecruiter?.toFixed(2) || '0.00'}</td>
                                                                    <td>{booth.interpreterMeetings || 0}</td>
                                                                    <td>{formatDuration(booth.interpreterTime || 0)}</td>
                                                                </tr>
                                                            ))}
                                                            {/* Totals Row */}
                                                            <tr className="totals-row">
                                                                <td><strong>TOTAL</strong></td>
                                                                <td>—</td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.jobSeekerInterest || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.uniqueQueueVisits || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.totalQueueVisits || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.uniqueMeetings || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.totalJobSeekerMeetings || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.droppedMeetings || 0), 0)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.meetingsOver3Min || 0), 0)}</strong></td>
                                                                <td><strong>{formatDuration(
                                                                    reportData.booths.reduce((sum, b) => sum + ((b.averageMeetingTime || 0) * (b.totalJobSeekerMeetings || 0)), 0) / 
                                                                    Math.max(reportData.booths.reduce((sum, b) => sum + (b.totalJobSeekerMeetings || 0), 0), 1)
                                                                )}</strong></td>
                                                                <td><strong>{(
                                                                    reportData.booths.reduce((sum, b) => sum + ((b.avgMeetingsPerRecruiter || 0) * (b.totalJobSeekerMeetings || 0)), 0) / 
                                                                    Math.max(reportData.booths.reduce((sum, b) => sum + (b.totalJobSeekerMeetings || 0), 0), 1)
                                                                ).toFixed(2)}</strong></td>
                                                                <td><strong>{reportData.booths.reduce((sum, b) => sum + (b.interpreterMeetings || 0), 0)}</strong></td>
                                                                <td><strong>{formatDuration(reportData.booths.reduce((sum, b) => sum + (b.interpreterTime || 0), 0))}</strong></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                </>
                                            ) : (
                                                <div className="analytics-empty-state">
                                                    <p>No booth data available</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Live Stats Tab */}
                                    {activeTab === 'live-stats' && (
                                        liveStatsLoading ? (
                                            <div className="analytics-loading">Loading live stats...</div>
                                        ) : liveStats ? (
                                            <div className="analytics-live-stats">
                                                <div className="stats-grid">
                                                    <div className="stat-card">
                                                        <h3>Users in Calls</h3>
                                                        <div className="stat-value">{liveStats.calls?.totalParticipants || 0}</div>
                                                    </div>
                                                    <div className="stat-card">
                                                        <h3>Active Sessions</h3>
                                                        <div className="stat-value">{liveStats.calls?.totalSessions || 0}</div>
                                                    </div>
                                                    <div className="stat-card">
                                                        <h3>Job Seekers in Queue</h3>
                                                        <div className="stat-value">{liveStats.queue?.totalWaiting || 0}</div>
                                                    </div>
                                                    <div className="stat-card">
                                                        <h3>Looking for Interpreter</h3>
                                                        <div className="stat-value">{liveStats.interpreters?.total || 0}</div>
                                                        <p className="stat-subtext">
                                                            {liveStats.interpreters?.waitingInCalls || 0} in calls, {liveStats.interpreters?.inQueue || 0} in queue
                                                        </p>
                                                    </div>
                                                    <div className="stat-card">
                                                        <h3>Total Users Online</h3>
                                                        <div className="stat-value">{liveStats.onlineUsers?.total || 0}</div>
                                                    </div>
                                                </div>

                                                <div className="live-stats-filters" role="search" aria-label="Filter live stats tables">
                                                    <div className="filter-group">
                                                        <label htmlFor="live-booth-filter">Booth</label>
                                                        <Select
                                                            id="live-booth-filter"
                                                            value={liveBoothFilter}
                                                            onChange={(e) => setLiveBoothFilter(e.target.value)}
                                                            placeholder="All booths"
                                                            options={liveBoothOptions}
                                                        />
                                                    </div>
                                                    <div className="filter-group">
                                                        <label htmlFor="live-name-filter">Job seeker name</label>
                                                        <input
                                                            id="live-name-filter"
                                                            type="search"
                                                            value={liveNameFilter}
                                                            onChange={(e) => setLiveNameFilter(e.target.value)}
                                                            autoComplete="off"
                                                            aria-describedby="live-filters-hint"
                                                        />
                                                    </div>
                                                    <div className="live-stats-checkbox">
                                                        <input
                                                            id="live-interpreter-filter"
                                                            type="checkbox"
                                                            checked={liveInterpreterOnly}
                                                            onChange={(e) => setLiveInterpreterOnly(e.target.checked)}
                                                        />
                                                        <label htmlFor="live-interpreter-filter">Only people looking for an interpreter</label>
                                                    </div>
                                                    {liveFiltersActive && (
                                                        <button
                                                            type="button"
                                                            className="btn-clear-live-filters"
                                                            onClick={() => {
                                                                setLiveBoothFilter('');
                                                                setLiveNameFilter('');
                                                                setLiveInterpreterOnly(false);
                                                            }}
                                                        >
                                                            Clear filters
                                                        </button>
                                                    )}
                                                    <p id="live-filters-hint" className="live-stats-filters-hint">
                                                        The name and interpreter filters apply to the call and queue tables. The totals above are for everything.
                                                    </p>
                                                    <p className="live-stats-filters-result" role="status" aria-live="polite">
                                                        {liveFiltersActive
                                                            ? `Showing ${visibleMeetings.length} of ${sortedMeetings.length} calls and ${visibleQueueEntries.length} of ${queueEntries.length} waiting job seekers.`
                                                            : ''}
                                                    </p>
                                                </div>

                                                <div className="live-stats-section">
                                                    <h2 id="live-calls-heading">Users in Calls</h2>
                                                    {visibleMeetings.length ? (
                                                        <div className="table-scroll-wrap" data-dual-scroll-target="true">
                                                            <table className="data-table live-stats-table" aria-labelledby="live-calls-heading">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Job Seeker</th>
                                                                        <th>Recruiter</th>
                                                                        <th>Interpreter</th>
                                                                        <th>Booth</th>
                                                                        <th>Joined</th>
                                                                        <th>Time in Call</th>
                                                                        {canForceEndCall && <th>Actions</th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleMeetings.map(meeting => (
                                                                        <tr key={meeting.sessionId}>
                                                                            <td>{meeting.jobSeeker || '—'}</td>
                                                                            <td>{meeting.recruiter || '—'}</td>
                                                                            <td>
                                                                                {meeting.interpreter
                                                                                    ? meeting.interpreter
                                                                                    : isLookingForInterpreter(meeting)
                                                                                        ? (
                                                                                            <span className="live-stats-needs-interpreter">
                                                                                                Requested{meeting.interpreterRequest.category ? ` (${meeting.interpreterRequest.category})` : ''}, waiting{' '}
                                                                                                <span className="live-stats-elapsed">{elapsedSince(meeting.interpreterRequest.requestedAt)}</span>
                                                                                            </span>
                                                                                        )
                                                                                        : '—'}
                                                                            </td>
                                                                            <td>{meeting.boothName || '—'}</td>
                                                                            <td>{formatDateTime(meeting.joinedAt)}</td>
                                                                            <td className="live-stats-elapsed">{elapsedSince(meeting.joinedAt)}</td>
                                                                            {canForceEndCall && (
                                                                                <td>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn-force-end-call"
                                                                                        onClick={() => handleForceEndCall(meeting)}
                                                                                        disabled={endingCallSessionId === meeting.sessionId}
                                                                                        aria-label={`Force end call${meeting.jobSeeker ? ` with ${meeting.jobSeeker}` : ''}`}
                                                                                    >
                                                                                        {endingCallSessionId === meeting.sessionId
                                                                                            ? 'Ending…'
                                                                                            : 'Force End Call'}
                                                                                    </button>
                                                                                </td>
                                                                            )}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="analytics-empty-state">
                                                            <p>{sortedMeetings.length ? 'No calls match these filters.' : 'No active call participants.'}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="live-stats-section">
                                                    <h2 id="live-queue-booth-heading">Queue by Booth</h2>
                                                    {visibleQueueByBooth.length ? (
                                                        <div className="table-scroll-wrap" data-dual-scroll-target="true">
                                                            <table className="data-table live-stats-table" aria-labelledby="live-queue-booth-heading">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Booth</th>
                                                                        <th>In Queue</th>
                                                                        <th>Invited</th>
                                                                        <th>Need Interpreter</th>
                                                                        <th>Longest Wait</th>
                                                                        <th>Average Wait</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleQueueByBooth.map(booth => (
                                                                        <tr key={booth.boothId || 'none'}>
                                                                            <td>{booth.boothName || '—'}</td>
                                                                            <td>{booth.total}</td>
                                                                            <td>{booth.invited}</td>
                                                                            <td>{booth.needInterpreter || 0}</td>
                                                                            <td className="live-stats-elapsed">{elapsedSince(booth.oldestJoinedAt)}</td>
                                                                            <td className="live-stats-elapsed">{elapsedSince(booth.averageJoinedAt)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="analytics-empty-state">
                                                            <p>{queueByBooth.length ? 'No booth queues match these filters.' : 'No one is waiting in any booth queue.'}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="live-stats-section">
                                                    <h2 id="live-waiting-heading">Job Seekers Waiting</h2>
                                                    {visibleQueueEntries.length ? (
                                                        <div className="table-scroll-wrap" data-dual-scroll-target="true">
                                                            <table className="data-table live-stats-table" aria-labelledby="live-waiting-heading">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Job Seeker</th>
                                                                        <th>Booth</th>
                                                                        <th>Event</th>
                                                                        <th>Status</th>
                                                                        <th>Interpreter</th>
                                                                        <th>Joined</th>
                                                                        <th>Time Waiting</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleQueueEntries.map(entry => (
                                                                        <tr key={entry.id}>
                                                                            <td>{entry.jobSeeker?.name || 'Unknown'}</td>
                                                                            <td>{entry.boothName || '—'}</td>
                                                                            <td>{entry.eventName || '—'}</td>
                                                                            <td>{entry.status}</td>
                                                                            <td>{entry.interpreterCategory || '—'}</td>
                                                                            <td>{formatDateTime(entry.joinedAt)}</td>
                                                                            <td className="live-stats-elapsed">{elapsedSince(entry.joinedAt)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="analytics-empty-state">
                                                            <p>{queueEntries.length ? 'No waiting job seekers match these filters.' : 'No job seekers currently waiting in queue.'}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="live-stats-section">
                                                    <h2 id="live-online-heading">Online Users</h2>
                                                    {visibleOnlineUsers.length ? (
                                                        <div className="table-scroll-wrap" data-dual-scroll-target="true">
                                                            <table className="data-table live-stats-table" aria-labelledby="live-online-heading">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Name</th>
                                                                        <th>Role</th>
                                                                        <th>Booth</th>
                                                                        <th>Chat Status</th>
                                                                        <th>Last Online</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleOnlineUsers.map(user => (
                                                                        <tr key={user.userId}>
                                                                            <td>{user.name || 'Unknown'}</td>
                                                                            <td>{user.role}</td>
                                                                            <td>{user.boothName || '—'}</td>
                                                                            <td>{CHAT_STATUS_LABELS[user.chatStatus] || '—'}</td>
                                                                            <td>{formatDateTime(user.lastOnline || user.connectedAt)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="analytics-empty-state">
                                                            <p>{onlineUserRows.length ? 'No online users match this booth filter.' : 'No users are currently online.'}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="analytics-empty-state">
                                                <p>Live stats are not available.</p>
                                            </div>
                                        )
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}

